import { currentWorkspaceSchema, workspaceOperationSchema } from '@mio/shared';
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';

import { apiRequest } from '@/shared/api/client';
import { supabase } from '@/shared/lib/supabase';

import {
  AuthContext,
  type AuthState,
  type AuthStatus,
  type SignOutGuard,
  type SignOutOptions,
} from './auth-context';
import { workspacePreparationOnSignOut } from './auth.flags';
import { authService } from './auth.service';
import { SignOutBlockedError } from './sign-out-blocked-error';

import type { WorkspaceOperation } from '@mio/shared';
import type { Session } from '@supabase/supabase-js';

function describeOperation(action: WorkspaceOperation['action']) {
  switch (action) {
    case 'checkpoint':
      return 'A backup';
    case 'submit':
      return 'Your submission';
    case 'activate':
      return 'Opening your workshop';
    case 'stop':
      return 'Pausing your workspace';
    default:
      return 'A workspace operation';
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<AuthStatus>('loading');
  const [session, setSession] = useState<Session | null>(null);
  const [signingOut, setSigningOut] = useState(false);
  const guards = useRef(new Map<SignOutGuard, 'workshop' | undefined>());
  const pendingSignOut = useRef<Promise<void> | null>(null);

  useEffect(() => {
    let cancelled = false;
    void supabase.auth.getSession().then(({ data }) => {
      if (cancelled) return;
      setSession(data.session);
      setStatus(data.session ? 'authenticated' : 'anonymous');
    });
    const { data: subscription } = supabase.auth.onAuthStateChange((_event, next) => {
      setSession(next);
      setStatus(next ? 'authenticated' : 'anonymous');
    });
    return () => {
      cancelled = true;
      subscription.subscription.unsubscribe();
    };
  }, []);

  /**
   * Poll one operation until it settles. The auth feature cannot import the
   * workshop feature's wait helper, so this keeps the same bound (five minutes).
   */
  const awaitOperation = useCallback(async (operation: WorkspaceOperation) => {
    const deadline = Date.now() + 5 * 60_000;
    let latest = operation;
    while (latest.status !== 'succeeded' && latest.status !== 'failed') {
      if (Date.now() >= deadline) throw new Error('operation still running');
      await new Promise<void>((resolve) => setTimeout(resolve, 1500));
      latest = await apiRequest(`/workshop/operations/${latest.id}`, workspaceOperationSchema, {
        signal: AbortSignal.timeout(15_000),
      });
    }
    return latest;
  }, []);
  const registerSignOutGuard = useCallback((guard: SignOutGuard, scope?: 'workshop') => {
    guards.current.set(guard, scope);
    return () => {
      guards.current.delete(guard);
    };
  }, []);
  /** Everything that must finish before the session ends; throws SignOutBlockedError otherwise. */
  const prepareSignOut = useCallback(async () => {
    // A direct/reloaded Overview has no mounted Workshop guard. Query the server
    // independently of route/query-cache state before considering sign-out safe.
    const current = await apiRequest('/workspaces/me', currentWorkspaceSchema, {
      signal: AbortSignal.timeout(10_000),
    }).catch(() => {
      throw new SignOutBlockedError(
        'Could not check your workspace. Retry when you are back online, or sign out anyway.',
        null,
      );
    });
    const activityId = current.activity?.id ?? null;
    if (current.busyOperation) {
      // A backup or save that is already running finishes on its own within a
      // bounded time; sign-out waits for it rather than sending the learner away.
      const finished = await awaitOperation(current.busyOperation).catch(() => null);
      const what = describeOperation(current.busyOperation.action);
      if (!finished)
        throw new SignOutBlockedError(
          `${what} is still finishing. Wait for it, then sign out again.`,
          activityId,
        );
      if (finished.status === 'failed')
        throw new SignOutBlockedError(
          `${what} did not complete. Check Workshop, then sign out again.`,
          activityId,
        );
    }
    const needsCheckpoint =
      current.enabled &&
      current.workspace?.activeAttemptId &&
      current.workspace.state !== 'stopped';
    if (needsCheckpoint && ![...guards.current.values()].includes('workshop'))
      throw new SignOutBlockedError(
        'Open Workshop to save your active IDE before signing out. You are still signed in.',
        activityId,
      );
    // Even stopped workspaces can have an unsaved explanation in a mounted page.
    const preparedWorkshopGuards = new Set<SignOutGuard>();
    for (const [guard, scope] of guards.current) {
      try {
        await guard(current);
      } catch (error) {
        if (scope === 'workshop' && error instanceof SignOutBlockedError)
          throw new SignOutBlockedError(error.message, activityId);
        throw error;
      }
      if (scope === 'workshop') preparedWorkshopGuards.add(guard);
    }
    if (
      needsCheckpoint &&
      ![...preparedWorkshopGuards].some((guard) => guards.current.get(guard) === 'workshop')
    )
      throw new SignOutBlockedError(
        'Workshop closed before sign-out finished. Open it and try again.',
        activityId,
      );
  }, [awaitOperation]);
  const signOut = useCallback(
    (options: SignOutOptions = {}) => {
      if (pendingSignOut.current) return pendingSignOut.current;
      setSigningOut(true);
      const pending = Promise.resolve().then(async () => {
        try {
          if (!options.force && workspacePreparationOnSignOut) await prepareSignOut();
          await authService.signOut();
        } finally {
          setSigningOut(false);
          pendingSignOut.current = null;
        }
      });
      pendingSignOut.current = pending;
      return pending;
    },
    [prepareSignOut],
  );

  const value = useMemo<AuthState>(
    () => ({
      status,
      session,
      user: session?.user ?? null,
      signOut,
      signingOut,
      registerSignOutGuard,
    }),
    [status, session, signOut, signingOut, registerSignOutGuard],
  );
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
